import React from "react";
import { ChevronUp, ChevronDown, Trash2, Plus } from "lucide-react";
import { getByPath } from "../lib/paths";
import { ImageField } from "./ImageField";
import { IconPicker } from "./IconPicker";
import { DEFAULT_ICON } from "../lib/treatmentIcons";
import { TreatmentAreasField } from "./TreatmentAreasField";

export const inputCls =
  "w-full rounded-lg border border-cream-2 px-3 py-2 text-sm text-ink outline-none focus:border-sage bg-white";

export function Field({ field, draft, update }) {
  const value = getByPath(draft, field.path);

  if (field.type === "treatmentareas") {
    return <TreatmentAreasField field={field} draft={draft} update={update} />;
  }

  if (field.type === "image") {
    return <ImageField label={field.label} value={value} onChange={(v) => update(field.path, v)} testId={`field-${field.path}`} />;
  }

  if (field.type === "stringlist") {
    const list = Array.isArray(value) ? value : [];
    return (
      <div>
        <p className="text-sm font-medium text-ink mb-2">{field.label}</p>
        <div className="space-y-2">
          {list.map((item, i) => (
            <textarea
              key={i}
              data-testid={`field-${field.path}-${i}`}
              value={item}
              onChange={(e) => update(field.path, list.map((v, idx) => (idx === i ? e.target.value : v)))}
              rows={item && item.length > 90 ? 3 : 1}
              className={inputCls}
            />
          ))}
        </div>
      </div>
    );
  }

  if (field.type === "objectlist") {
    const list = Array.isArray(value) ? value : [];
    const move = (i, dir) => {
      const j = i + dir;
      if (j < 0 || j >= list.length) return;
      const next = list.slice();
      [next[i], next[j]] = [next[j], next[i]];
      update(field.path, next);
    };
    const removeItem = (i) => update(field.path, list.filter((_, idx) => idx !== i));
    const addItem = () => {
      const blank = {};
      field.itemFields.forEach((f) => { blank[f.key] = f.type === "icon" ? DEFAULT_ICON : ""; });
      update(field.path, [...list, blank]);
    };
    const setItem = (i, key, val) =>
      update(field.path, list.map((v, idx) => (idx === i ? { ...v, [key]: val } : v)));

    return (
      <div>
        <p className="text-sm font-medium text-ink mb-2">{field.label}</p>
        <div className="space-y-3">
          {list.map((obj, i) => (
            <div key={i} data-testid={`${field.path}-item-${i}`} className="rounded-lg border border-cream-2 p-3 bg-cream/40">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-ink-muted">#{i + 1}</span>
                <div className="flex items-center gap-1">
                  <button type="button" data-testid={`${field.path}-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className="p-1.5 rounded-md text-ink-soft hover:bg-cream-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button type="button" data-testid={`${field.path}-down-${i}`} onClick={() => move(i, 1)} disabled={i === list.length - 1} title="Move down" className="p-1.5 rounded-md text-ink-soft hover:bg-cream-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button type="button" data-testid={`${field.path}-remove-${i}`} onClick={() => removeItem(i)} title="Delete" className="p-1.5 rounded-md text-red-600 hover:bg-red-50 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {field.itemFields.map((f) => (
                  <div key={f.key} className={f.type === "icon" ? "sm:col-span-2" : ""}>
                    <label className="block text-xs text-ink-muted mb-1">{f.label}</label>
                    {f.type === "icon" ? (
                      <IconPicker value={obj[f.key]} onChange={(v) => setItem(i, f.key, v)} testId={`field-${field.path}-${i}-${f.key}`} />
                    ) : f.type === "toggle" ? (
                      <button
                        type="button"
                        data-testid={`field-${field.path}-${i}-${f.key}`}
                        aria-pressed={obj[f.key] !== false}
                        onClick={() => setItem(i, f.key, obj[f.key] === false ? true : false)}
                        className={`inline-flex h-6 w-11 items-center rounded-full transition-colors ${obj[f.key] !== false ? "bg-sage" : "bg-cream-2"}`}
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${obj[f.key] !== false ? "translate-x-5" : "translate-x-0.5"}`} />
                      </button>
                    ) : (
                      <input
                        data-testid={`field-${field.path}-${i}-${f.key}`}
                        value={obj[f.key] ?? ""}
                        onChange={(e) => setItem(i, f.key, e.target.value)}
                        className={inputCls}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <button type="button" data-testid={`${field.path}-add`} onClick={addItem} className="mt-3 inline-flex items-center gap-2 rounded-full border border-sage/40 text-sage-dark px-4 py-2 text-sm font-semibold hover:bg-sage/10 transition-colors">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
    );
  }

  if (field.type === "navlist") {
    const list = Array.isArray(value) ? value : [];
    return (
      <div>
        <p className="text-sm font-medium text-ink mb-2">{field.label}</p>
        <p className="text-xs text-ink-muted mb-3">Rename the text shown in the menu. The section each item scrolls to is fixed.</p>
        <div className="space-y-2">
          {list.map((item, i) => (
            <div key={item.id || i} className="flex items-center gap-3 rounded-lg border border-cream-2 bg-cream/40 px-3 py-2">
              <span className="text-xs font-mono text-ink-muted shrink-0 w-24">#{item.id}</span>
              <input
                data-testid={`field-${field.path}-${item.id}-label`}
                value={item.label ?? ""}
                onChange={(e) => update(field.path, list.map((v, idx) => (idx === i ? { ...v, label: e.target.value } : v)))}
                className={inputCls}
                placeholder="Menu label"
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === "toggle") {
    const on = value !== false;
    return (
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-ink">{field.label}</label>
        <button
          type="button"
          data-testid={`field-${field.path}`}
          aria-pressed={on}
          onClick={() => update(field.path, !on)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? "bg-sage" : "bg-cream-2"}`}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
      </div>
    );
  }

  if (field.type === "icon") {
    return (
      <div>
        <label className="block text-sm font-medium text-ink mb-2">{field.label}</label>
        <IconPicker value={value} onChange={(v) => update(field.path, v)} testId={`field-${field.path}`} />
      </div>
    );
  }

  const Tag = field.type === "textarea" ? "textarea" : "input";
  return (
    <div>
      <label className="block text-sm font-medium text-ink mb-2">{field.label}</label>
      <Tag
        data-testid={`field-${field.path}`}
        value={value ?? ""}
        onChange={(e) => update(field.path, e.target.value)}
        rows={field.type === "textarea" ? 3 : undefined}
        className={inputCls}
      />
    </div>
  );
}

export default Field;
